import { Chart } from 'components/Chart/Chart'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { COLORS } from 'modules/Statistics/colors'
import StatisticsLayout from 'modules/Statistics/Layout'
import { ChartLegend } from 'modules/Statistics/Layout/ChartLegend'
import styles from 'modules/Statistics/statistics.module.scss'
import useFiltersManager from 'utils/hooks/useFiltersManager'
import config from 'modules/Statistics/Main/filters'
import FilterPanel from 'components/Filters/FilterPanel'
import omit from 'lodash/omit'
import isEqual from 'lodash/isEqual'
import { Button, Grid } from 'gu-ui-kit'
import { useTranslation } from 'react-i18next'
import { useLocalStorage } from 'utils/hooks/useLocalStorage'
import Switch from 'components/Switch'
import STATISTICS_API from 'api/statistics/v2'
import TableComponent from './subcomponents/TableComponent'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import PageHeader from 'components/PageHeader'

const StatisticsMain = ({ drawerState = false }) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const breakpoint = useBreakpoint()

  const { chart, i18n, filters } = useSelector(({ statistics, app }) => ({
    ...statistics,
    ...app,
  }))

  const [isDemoMode, setIsDemoMode] = useLocalStorage('isDemoMode', false)

  const filterManager = useFiltersManager({
    filters: filters,
    config: config(),
    additionalFilterState: {
      ...filters,
      ...isDemoMode,
    },
    setAction: 'STATISTICS/SET_FILTER',
    nonClearableFilters: ['date_group_period', 'include_vat'],
    localStorageKey: 'main-statistics',
  })

  useEffect(
    () =>
      STATISTICS_API.fetchAccounts().then(({ response, error }) => {
        if (!error) {
          const { data = [] } = response

          filterManager.updateConfiguration({
            key: 'account_ids',
            val: {
              options: data.map(({ login, id }) => ({
                value: id,
                label: login,
              })),
            },
          })
        }
      }),
    [],
  )

  const [filtersState, setAccountFiltersState] = useState(
    omit(filterManager.filters, ['include_vat']),
  )

  useEffect(() => {
    const omittedFilters = omit(filterManager.filters, ['include_vat'])
    !isEqual(filtersState, omittedFilters) && setAccountFiltersState(omittedFilters)
  }, [filterManager.filters])

  const changeChartType = useCallback(
    () =>
      dispatch({
        type: 'STATISTICS/SET_CHART_TYPE',
        payload: { type: chart.chartType === 'Line' ? 'Bar' : 'Line' },
      }),
    [chart],
  )

  useEffect(
    () => filterManager.filters && dispatch({ type: 'STATISTICS/FETCH_CHART', isDemoMode }),
    [filterManager.filters, isDemoMode],
  )

  return (
    <>
      <Grid
        className={'margin-bottom-24'}
        justifyContent={breakpoint.fbs ? 'between' : 'start'}
        alignItems={'center'}
      >
        <PageHeader color={'Label-Primary'}>{t('common::nav_menu.statistics')}</PageHeader>

        <Switch
          label={t('statistics::demo_mode_switcher')}
          checked={isDemoMode}
          onChange={() => {
            setIsDemoMode(!isDemoMode)
          }}
          className={styles['demo-switcher']}
        />
      </Grid>
      <StatisticsLayout
        drawerState={drawerState}
        FilterPanel={
          filterManager.filters && (
            <FilterPanel filterManager={filterManager} className={styles['filter-panel']} />
          )
        }
        ChartComponent={
          <Chart
            chartLegend={
              <ChartLegend
                measure={chart.measure}
                metrics={chart.metrics}
                summary={chart.summary}
                include_vat={filterManager.filters?.include_vat}
                handleClick={(id) =>
                  dispatch({
                    type: 'STATISTICS/TOGGLE_METRIC_HIDDEN',
                    payload: { id },
                  })
                }
              />
            }
            i18n={i18n}
            emptyDataLabel={{
              text: (
                <div className={styles['chart__empty-text']}>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: t('statistics::chart.empty_data.text', {
                        context: isDemoMode ? 'demo' : '',
                      }),
                    }}
                  />

                  {!isDemoMode && (
                    <Button variant={'primary'} size={'m'} onClick={() => setIsDemoMode(true)}>
                      {t('statistics::demo_mode_switcher')}
                    </Button>
                  )}
                </div>
              ),
            }}
            colors={COLORS}
            className={styles['chart']}
            chartHeaderClassName={styles['chart-header']}
            chartType={chart.chartType}
            items={chart.data}
            metrics={chart.metrics}
            loading={chart.loading}
            date_group_period={{
              value: filterManager.filters?.date_group_period ?? 'day',
              options: filterManager.configuration.date_group_period.options,
            }}
            include_vat={filterManager.filters?.include_vat}
            summary={chart.summary}
            measure={chart.measure}
            changeChartPeriod={(value) =>
              filterManager.updateFilter({
                fieldName: 'date_group_period',
                value: value,
              })
            }
            changeChartType={changeChartType}
          />
        }
        TableComponent={
          filterManager.filters && (
            <TableComponent filters={filterManager.filters} isDemoMode={isDemoMode} />
          )
        }
      />
    </>
  )
}

StatisticsMain.propTypes = {
  drawerState: PropTypes.bool,
}

export default StatisticsMain
